import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable no-case-declarations */
/* eslint-disable max-lines */
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, Grid, MobileStepper, Typography, } from '@mui/material';
import { useCallback, useContext, useMemo, useReducer, useRef, useState, } from 'react';
import { EmployeeApi } from '../api';
import { isBreakpointMobile, useBreakpoint } from '../breakpoint';
import { CalendarContext } from '../components';
import { useSettings, useUser } from '../hooks';
import { ReducerContext as AbdooReducerContext } from '../hooks/reducer';
import { initialManagerState } from '../types/userTypes';
import { EntitlementPage, FinishPage, HolidayYearPage, ManagerPage, WelcomePage, } from './pages';
import { entitlementExceptionsReducer, entitlementReducer, holidayYearReducer, initialEntitlementExceptionsState, initialEntitlementState, initialHolidayYearState, IntroSetupContext, managerReducer, } from './reducer';
const PAGES = {
    WELCOME: 'Welcome to Abdoo!',
    HOLIDAY_YEAR: 'Entitlement year and working pattern',
    ENTITLEMENT: 'Entitlement and Bank Holidays',
    MANAGER: 'Approval requests',
    FINISH: 'Finish',
};
const frePages = [
    { label: PAGES.WELCOME, optional: false },
    { label: PAGES.HOLIDAY_YEAR, optional: false },
    { label: PAGES.ENTITLEMENT, optional: false },
    { label: PAGES.MANAGER, optional: true },
    { label: PAGES.FINISH, optional: false },
];
const holidayYearPages = [
    { label: PAGES.HOLIDAY_YEAR, optional: false },
    { label: PAGES.ENTITLEMENT, optional: false },
    { label: PAGES.FINISH, optional: false },
];
const managerPages = [
    { label: PAGES.MANAGER, optional: false },
    { label: PAGES.FINISH, optional: false },
];
const getInitialHolidayYearState = (isFre, userSettingsState) => {
    return isFre
        ? initialHolidayYearState
        : {
            ...initialHolidayYearState,
            end: userSettingsState.end,
            workingDays: {
                ...userSettingsState.workingDays,
            },
        };
};
const getInitialEntitlementState = (isFre, userSettingsState) => {
    return isFre
        ? initialEntitlementState
        : {
            ...initialEntitlementState,
            remainingEntitlement: userSettingsState.remainingHolidays,
            totalEntitlement: userSettingsState.entitlement,
        };
};
const getInitialEntitlementExceptionState = (isFre, userSettingsState) => {
    return isFre
        ? initialEntitlementExceptionsState
        : {
            ...initialEntitlementExceptionsState,
            regionCode: userSettingsState.regionCode,
            countryCode: userSettingsState.countryCode,
        };
};
const getInitialManagerState = (isFre, userSettingsState) => {
    return isFre
        ? initialManagerState
        : {
            ...initialManagerState,
            managerName: userSettingsState.managerName,
            managerEmail: userSettingsState.managerEmail,
            knownAsName: userSettingsState.knownAsName,
            requestManager: !!userSettingsState.managerEmail,
            managerSharingDataOption: userSettingsState.managerSharingDataOption,
        };
};
const getIntroDialogPages = (type) => {
    switch (type) {
        case 'fre':
            return frePages;
        case 'manageHolidayYear':
            return holidayYearPages;
        case 'manager':
            return managerPages;
        default:
            throw new Error('Invalid type');
    }
};
export const IntroDialog = ({ open, type, finish }) => {
    const isFre = type === 'fre';
    const pages = getIntroDialogPages(type);
    const [page, setPage] = useState(0);
    const [skipped, setSkipped] = useState(new Set());
    const { userSettingsState } = useContext(AbdooReducerContext);
    const [holidayYearState, holidayYearDispatch] = useReducer(holidayYearReducer, getInitialHolidayYearState(isFre, userSettingsState));
    const [entitlementState, entitlementDispatch] = useReducer(entitlementReducer, getInitialEntitlementState(isFre, userSettingsState));
    const [entitlementExceptionsState, entitlementExceptionsDispatch] = useReducer(entitlementExceptionsReducer, getInitialEntitlementExceptionState(isFre, userSettingsState));
    const [managerState, managerDispatch] = useReducer(managerReducer, getInitialManagerState(isFre, userSettingsState));
    const { onSubmitHolidayYearForm } = useContext(CalendarContext);
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    const holidayYearPageRef = useRef();
    const entitlementPageRef = useRef();
    const managerPageRef = useRef();
    const [holidayYearPageFirstLoad, setHolidayYearPageFirstLoad] = useState(true);
    const [entitlementPageFirstLoad, setEntitlementPageFirstLoad] = useState(true);
    const [isFinishing, setIsFinishing] = useState(false);
    const isStepSkipped = useCallback((step) => {
        return skipped.has(step);
    }, [skipped]);
    const breakpoint = useBreakpoint();
    const isMobile = isBreakpointMobile(breakpoint);
    const showBackButton = page > 0 || type !== 'fre';
    const showFinishButton = page === pages.length - 1;
    const reducerContextValue = useMemo(() => {
        return {
            holidayYearState,
            holidayYearDispatch,
            entitlementState,
            entitlementDispatch,
            entitlementExceptionsState,
            entitlementExceptionsDispatch,
            managerState,
            managerDispatch,
        };
    }, [
        holidayYearState,
        holidayYearDispatch,
        entitlementState,
        entitlementDispatch,
        entitlementExceptionsState,
        entitlementExceptionsDispatch,
        managerState,
        managerDispatch,
    ]);
    const getCurrentPage = useCallback(() => {
        switch (type) {
            case 'fre':
                return frePages[page].label;
            case 'manageHolidayYear':
                return holidayYearPages[page].label;
            case 'manager':
                return managerPages[page].label;
            default:
                throw new Error('Invalid type');
        }
    }, [page, type]);
    const onNext = useCallback(() => {
        switch (getCurrentPage()) {
            case PAGES.HOLIDAY_YEAR:
                const isHolidayYearPageValid = holidayYearPageRef.current.getIsValid();
                holidayYearPageRef.current.handleSubmit();
                if (!isHolidayYearPageValid) {
                    return;
                }
                setHolidayYearPageFirstLoad(false);
                break;
            case PAGES.ENTITLEMENT:
                const isEntitlementPageValid = entitlementPageRef.current.getIsValid();
                entitlementPageRef.current.handleSubmit();
                if (!isEntitlementPageValid) {
                    return;
                }
                setEntitlementPageFirstLoad(false);
                break;
            case PAGES.MANAGER:
                const isManagerPageValid = managerPageRef.current.getIsValid();
                managerPageRef.current.handleSubmit();
                if (!isManagerPageValid) {
                    return;
                }
                break;
            default:
                break;
        }
        let newSkipped = skipped;
        if (isStepSkipped(page)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(page);
        }
        setPage(page + 1);
        setSkipped(newSkipped);
    }, [getCurrentPage, skipped, isStepSkipped, page]);
    const onBack = useCallback(() => {
        if (isFre === false && page === 0) {
            if (finish) {
                finish();
                return;
            }
        }
        setPage(page - 1);
    }, [isFre, page, finish]);
    const { submitUserSettings } = useSettings(EmployeeApi);
    const { managerDetails } = useUser(EmployeeApi);
    const cleanup = useCallback(() => {
        setIsFinishing(true);
        onSubmitHolidayYearForm();
        if (finish) {
            finish();
            setPage(0);
        }
    }, [finish, onSubmitHolidayYearForm]);
    const onFinish = useCallback(async () => {
        const initialSettings = {
            end: holidayYearState.end,
            entitlement: entitlementState.totalEntitlement,
            remainingHolidays: entitlementState.remainingEntitlement,
            regionCode: entitlementExceptionsState.regionCode,
            countryCode: entitlementExceptionsState.countryCode,
            knownAsName: managerState.requestManager ? managerState.knownAsName : '',
            managerName: managerState.requestManager ? managerState.managerName : '',
            managerEmail: managerState.requestManager ? managerState.managerEmail : '',
            timeAdded: new Date(),
            doNotShowSetupYearDialog: false,
            managerSharingDataOption: managerState.managerSharingDataOption,
            workingDays: {
                ...holidayYearState.workingDays,
            },
        };
        await submitUserSettings().mutationAsync(initialSettings, cleanup);
    }, [
        holidayYearState.end,
        holidayYearState.workingDays,
        entitlementState.totalEntitlement,
        entitlementState.remainingEntitlement,
        entitlementExceptionsState.regionCode,
        entitlementExceptionsState.countryCode,
        managerState.requestManager,
        managerState.knownAsName,
        managerState.managerName,
        managerState.managerEmail,
        managerState.managerSharingDataOption,
        submitUserSettings,
        cleanup,
    ]);
    const CurrentPage = useCallback(() => {
        switch (getCurrentPage()) {
            case PAGES.WELCOME:
                return _jsx(WelcomePage, {});
            case PAGES.HOLIDAY_YEAR:
                return (_jsx(HolidayYearPage, { ref: holidayYearPageRef, initial: isFre && holidayYearPageFirstLoad }));
            case PAGES.ENTITLEMENT:
                return (_jsx(EntitlementPage, { ref: entitlementPageRef, initial: isFre && entitlementPageFirstLoad, endDate: holidayYearState.end }));
            case PAGES.MANAGER:
                return (_jsx(ManagerPage, { ref: managerPageRef, initialManagerDetails: managerDetails.data }));
            case PAGES.FINISH:
                return _jsx(FinishPage, {});
            default:
                return _jsx(_Fragment, {});
        }
    }, [
        entitlementPageFirstLoad,
        getCurrentPage,
        holidayYearPageFirstLoad,
        holidayYearState.end,
        isFre,
        managerDetails.data,
    ]);
    return (_jsx("div", { children: _jsx(IntroSetupContext.Provider, { value: reducerContextValue, children: _jsxs(Dialog, { open: open, fullWidth: true, fullScreen: isMobile, scroll: "paper", children: [_jsxs(DialogContent, { style: { overflow: 'auto' }, children: [_jsx(Grid, { children: _jsxs(Grid, { item: true, children: [_jsx(Typography, { variant: "h6", align: "center", fontWeight: "bold", sx: {
                                                marginTop: '0.9em',
                                                visibility: pages[page].label ? 'visible' : 'hidden',
                                            }, children: pages[page].label }), pages[page].optional && (_jsx(Typography, { align: "center", variant: "body2", children: "Optional" }))] }) }), _jsx(Grid, { container: true, justifyContent: "center", height: "auto", children: _jsx(Grid, { item: true, xs: 10, sx: { marginTop: isMobile ? '1em' : '0em' }, children: _jsx(CurrentPage, {}) }) })] }), _jsx(DialogActions, { children: _jsx(MobileStepper, { variant: "dots", steps: pages.length, position: "static", activeStep: page, sx: { flexGrow: 1 }, nextButton: showFinishButton ? (_jsxs(LoadingButton, { size: "small", onClick: onFinish, loading: submitUserSettings().isLoading || isFinishing, children: ["Finish ", _jsx(KeyboardArrowRight, {})] })) : (_jsxs(Button, { size: "small", onClick: onNext, children: ["Next ", _jsx(KeyboardArrowRight, {})] })), backButton: _jsxs(Button, { size: "small", onClick: onBack, sx: { visibility: showBackButton ? 'visible' : 'hidden' }, children: [_jsx(KeyboardArrowLeft, {}), " Back"] }) }) })] }) }) }));
};
